<template>
  <div class="festival-history iabf-dancer-theme">
    <div v-if="loading" class="history-loading">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <p v-else-if="loadError" class="history-error" role="alert">{{ loadError }}</p>

    <p v-else-if="!yearPanels.length" class="history-empty">
      We don’t have past festival years on file yet.
      If that looks wrong, email
      <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
    </p>

    <div v-else class="year-list">
      <section
        v-for="panel in yearPanels"
        :key="panel.year"
        class="year-panel iabf-card"
        :class="{ 'is-open': isOpen(panel.year) }"
      >
        <button
          type="button"
          class="year-panel-toggle"
          :aria-expanded="isOpen(panel.year) ? 'true' : 'false'"
          @click="toggleYear(panel.year)"
        >
          <span class="year-panel-label">
            <strong>IABF {{ panel.year }}</strong>
            <span class="year-summary muted">{{ yearSummary(panel) }}</span>
          </span>
          <span class="year-chev" aria-hidden="true">{{ isOpen(panel.year) ? '▴' : '▾' }}</span>
        </button>

        <div v-if="isOpen(panel.year)" class="year-panel-body">
          <div class="history-block">
            <h3 class="history-block-title">Showcase</h3>
            <p v-if="!panel.pieces.length" class="muted small">
              No showcase pieces on file for this year.
            </p>
            <ul v-else class="history-rows">
              <li v-for="p in panel.pieces" :key="p.id" class="history-row">
                <div class="history-row-main">
                  <span class="history-row-name">{{ p.title }}</span>
                  <span class="history-row-meta muted small">
                    {{ p.pieceType }}
                    <template v-if="p.competition && p.competition !== 'None'">
                      · {{ p.competition }}
                    </template>
                  </span>
                </div>
                <a
                  v-if="p.performanceRecordingUrl"
                  :href="p.performanceRecordingUrl"
                  target="_blank"
                  rel="noopener"
                  class="history-watch"
                >Watch →</a>
                <span v-else class="muted small">No recording yet</span>
              </li>
            </ul>
          </div>

          <div v-if="panel.corpsPieces && panel.corpsPieces.length" class="history-block">
            <h3 class="history-block-title">Corps de ballet</h3>
            <ul class="history-rows">
              <li
                v-for="g in panel.corpsPieces"
                :key="'corps-' + panel.year + '-' + g.id"
                class="history-row"
              >
                <div class="history-row-main">
                  <span class="history-row-name">{{ g.name }}</span>
                  <span v-if="g.role" class="history-row-meta muted small">{{ g.role }}</span>
                </div>
                <a
                  v-if="g.performanceRecordingUrl"
                  :href="g.performanceRecordingUrl"
                  target="_blank"
                  rel="noopener"
                  class="history-watch"
                >Watch →</a>
                <span v-else class="muted small">No recording yet</span>
              </li>
            </ul>
          </div>

          <div v-if="panel.competition && panel.competition.length" class="history-block">
            <h3 class="history-block-title">Competition</h3>
            <ul class="history-rows">
              <li
                v-for="(r, i) in panel.competition"
                :key="'c-' + panel.year + '-' + i"
                class="history-row history-row--award"
              >
                <div class="history-row-main">
                  <span class="history-row-name">
                    {{ r.placement || 'Result' }}
                  </span>
                  <span class="history-row-meta muted small">
                    <template v-if="r.pieceTitle">{{ r.pieceTitle }}</template>
                    <template v-if="r.division">
                      <template v-if="r.pieceTitle"> · </template>{{ r.division }}
                    </template>
                    <template v-if="r.score">
                      <template v-if="r.pieceTitle || r.division"> · </template>{{ r.score }}
                    </template>
                  </span>
                  <span v-if="r.notes" class="history-row-notes muted small">{{ r.notes }}</span>
                </div>
                <span class="award-badge" aria-hidden="true">🏅</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>

    <p v-if="!loading && yearPanels.length" class="history-footer">
      Missing a year or a recording?
      <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>
    </p>
  </div>
</template>

<script>
import { IABF_DANCER_YEAR } from '@/config/iabf-event'

export default {
  name: 'FestivalHistory',
  data () {
    return {
      loading: true,
      loadError: '',
      yearPanels: [],
      openYears: {}
    }
  },
  computed: {
    user () {
      return this.$store.getters.user
    },
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    person () {
      return this.$store.getters.currentIABFPerson || {}
    },
    historyIdentity () {
      return [
        (this.dancer && this.dancer.id) || '',
        (this.person && this.person.id) || '',
        (this.user && this.user.id) || ''
      ].join('|')
    }
  },
  watch: {
    historyIdentity: {
      immediate: true,
      handler () {
        if (this.user || (this.dancer && this.dancer.id) || (this.person && this.person.id)) {
          this.loadHistory()
        }
      }
    }
  },
  methods: {
    isOpen (year) {
      return !!this.openYears[year]
    },
    toggleYear (year) {
      this.$set(this.openYears, year, !this.openYears[year])
    },
    yearSummary (panel) {
      const parts = []
      const n = (panel.pieces && panel.pieces.length) || 0
      const g = (panel.corpsPieces && panel.corpsPieces.length) || 0
      const c = (panel.competition && panel.competition.length) || 0
      if (n) {
        parts.push(n === 1 ? '1 showcase piece' : `${n} showcase pieces`)
      }
      if (g) {
        parts.push(g === 1 ? '1 group piece' : `${g} group pieces`)
      }
      if (c) {
        parts.push(c === 1 ? '1 result' : `${c} results`)
      }
      if (!parts.length) {
        return panel.isRegistered ? 'Registered' : 'No details yet'
      }
      return parts.join(' · ')
    },
    async loadHistory () {
      this.loading = true
      this.loadError = ''
      try {
        if (
          this.user &&
          this.user.id &&
          !this.$store.state.masqueradeDancerId &&
          !(this.person && this.person.id)
        ) {
          try {
            await this.$store.dispatch('loadIABFDancerByUid', this.user.id)
          } catch (e) { /* non-fatal */ }
        }
        const liveDancer = this.$store.getters.currentIABFDancer || {}
        const livePerson = this.$store.getters.currentIABFPerson || {}
        const hasLiveEnrollment = !!(liveDancer.id && !liveDancer.isCanceledAccount)
        const masquerading = !!this.$store.state.masqueradeDancerId
        const result = await this.$store.dispatch('loadDancerFestivalHistory', {
          email: liveDancer.email || (!masquerading && this.user && this.user.email) || '',
          firstName: liveDancer.firstName || (!masquerading && livePerson.firstName) || '',
          lastName: liveDancer.lastName || (!masquerading && livePerson.lastName) || '',
          yearsAttended: liveDancer.yearsAttended || (!masquerading && livePerson.yearsAttended) || [],
          enrollmentYear: hasLiveEnrollment
            ? (liveDancer.festivalYear || this.$store.getters.iabfDancerYear)
            : null,
          personId: liveDancer.personId || (!masquerading && livePerson.id) || null
        })
        // History is past festivals — this year's prep lives on the dashboard cards.
        const years = ((result && result.years) || []).filter(p => Number(p.year) !== IABF_DANCER_YEAR)
        this.yearPanels = years
        const open = {}
        const prefer = years.find(p => p.hasContent) || years[0]
        if (prefer) {
          open[prefer.year] = true
        }
        this.openYears = open
      } catch (e) {
        console.warn('History load failed', e)
        this.loadError = 'Could not load your festival history. Try again in a moment.'
        this.yearPanels = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.history-loading {
  padding: 32px 0;
  text-align: center;
}
.history-error,
.history-empty {
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.5;
}
.year-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.year-panel {
  padding: 0;
  overflow: hidden;
}
.year-panel-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
}
.year-panel-toggle:hover {
  background: var(--iabf-bg-soft, #eef0eb);
}
.year-panel-label {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 12px;
  min-width: 0;
}
.year-panel-label strong {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 18px;
  color: var(--iabf-ink, #1c1b1f);
}
.year-chip {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--iabf-secondary-soft, #def0ef);
  color: var(--iabf-secondary, #267E7D);
}
.year-summary {
  font-size: 14px;
}
.year-chev {
  color: var(--iabf-ink-faint, #8a8792);
  flex-shrink: 0;
}
.year-panel-body {
  padding: 0 18px 18px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.history-block {
  margin-top: 16px;
}
.history-block-title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 10px;
}
.history-rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.history-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--iabf-bg, #fef9f5);
  border: 1px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
}
.history-row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.history-row-name {
  font-weight: 600;
  color: var(--iabf-ink, #1c1b1f);
}
.history-watch {
  color: var(--iabf-primary, #8a957d);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}
.history-watch:hover {
  text-decoration: underline;
}
.award-badge {
  font-size: 18px;
}
.muted {
  color: var(--iabf-ink-muted, #5c5a63);
}
.small {
  font-size: 13px;
}
.history-footer {
  margin-top: 28px;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
}
</style>
